import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
} from 'firebase/auth';
import { isFirebaseConfigured, resolveFirebaseAuthDomain } from '@/utils/firebase-config';
import { registerAuthTeardown } from '@/utils/auth-teardown';

export { isFirebaseConfigured, resolveFirebaseAuthDomain };

const GOOGLE_REDIRECT_PENDING_KEY = 'ds-firebase-google-redirect';
const LAST_GOOGLE_EMAIL_KEY = 'ds-last-google-email';
const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let authInstance = null;
/** @type {string} */
let cachedGoogleWebClientId = '';
/** @type {Promise<void> | null} */
let gisScriptPromise = null;
/** 已对哪个 clientId 做过 GIS initialize（整页只应一次） */
let gisInitializedClientId = '';
let gisInitializedLoginHint = '';
/** @type {null | ((response: { credential?: string }) => void)} */
let oneTapCredentialHandler = null;
/** 进行中的 Google One Tap 取消函数（清定时器 + reject） */
let inFlightGoogleCancel = null;
/** Redirect 结果整页只探测、消费一次，避免重复请求平台 Google 登录。 */
let googleRedirectResultPromise = null;
let googleRedirectResultConsumed = false;

/** One Tap：未展示前的短超时 */
const ONE_TAP_PRE_DISPLAY_MS = 2500;
/** One Tap：未展示时的硬超时，超时后 fallback popup。已展示则取消，避免打断选号。 */
const ONE_TAP_HARD_TIMEOUT_MS = 4500;

/**
 * Edge Chromium：FedCM One Tap 易误报 display / cooldown 无 UI，且多次 initialize 更糟。
 * 直接走 popup，保留用户点击手势。
 */
function shouldSkipGoogleOneTap() {
  if (typeof navigator === 'undefined') return false;
  return /Edg\//i.test(String(navigator.userAgent || ''));
}

/**
 * 用户点「取消」或关窗：中断 One Tap 等待（不 abort 已打开的 OAuth popup）。
 */
export function cancelInFlightGoogleSignIn() {
  try {
    window.google?.accounts?.id?.cancel?.();
  } catch {
    /* ignore */
  }
  const cancel = inFlightGoogleCancel;
  inFlightGoogleCancel = null;
  oneTapCredentialHandler = null;
  if (typeof cancel === 'function') cancel();
}

/**
 * 清掉 GIS「可自动选号」记忆。
 * 登出路径调用；账号切换已跳过 One Tap。脚本未加载时 no-op。
 */
export function disableGoogleOneTapAutoSelect() {
  try {
    window.google?.accounts?.id?.disableAutoSelect?.();
  } catch {
    /* ignore */
  }
}

export function getLastGoogleEmail() {
  return readLastGoogleEmail();
}

function getFirebaseConfig() {
  const hostname = typeof window !== 'undefined' ? String(window.location?.hostname || '') : '';
  // 按页面域动态选 authDomain（live→login.deepsage.live，app→login.deepsage.app）；不要尾斜杠
  const authDomain = resolveFirebaseAuthDomain(hostname, process.env.VUE_APP_FIREBASE_AUTH_DOMAIN);
  return {
    apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
    authDomain,
    projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.VUE_APP_FIREBASE_APP_ID,
  };
}

export function getFirebaseAuth() {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase is not configured');
  }
  if (!authInstance) {
    const apps = getApps();
    const app = apps.length ? apps[0] : initializeApp(getFirebaseConfig());
    authInstance = getAuth(app);
  }
  return authInstance;
}

function readLastGoogleEmail() {
  if (typeof localStorage === 'undefined') return '';
  try {
    return String(localStorage.getItem(LAST_GOOGLE_EMAIL_KEY) ?? '').trim();
  } catch {
    return '';
  }
}

function rememberLastGoogleEmail(email) {
  const value = String(email ?? '').trim();
  if (!value || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LAST_GOOGLE_EMAIL_KEY, value);
  } catch {
    /* ignore quota */
  }
}

function emailFromJwt(idToken) {
  try {
    const payload = String(idToken || '').split('.')[1] || '';
    if (!payload) return '';
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const data = JSON.parse(json);
    return String(data?.email ?? '').trim();
  } catch {
    return '';
  }
}

function normalizeGoogleFlow(flow) {
  return flow === 'remembered' ? 'remembered' : 'chooser';
}

/**
 * Popup / Redirect：remembered 只提示指定账号；chooser 强制选号且绝不带 login_hint。
 */
function createGoogleProvider({ loginHint, flow = 'chooser' } = {}) {
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  const mode = normalizeGoogleFlow(flow);
  const hint = mode === 'remembered' ? String(loginHint || '').trim() : '';
  const params = {};
  if (mode === 'chooser') {
    params.prompt = 'select_account';
  }
  if (hint) {
    params.login_hint = hint;
  }
  if (Object.keys(params).length) {
    provider.setCustomParameters(params);
  }
  return provider;
}

function extractGoogleIdToken(result) {
  if (!result) return '';
  const credential = GoogleAuthProvider.credentialFromResult(result);
  const fromCred = String(credential?.idToken ?? '').trim();
  if (fromCred) return fromCred;
  const fromResponse = String(result?._tokenResponse?.oauthIdToken ?? '').trim();
  return fromResponse;
}

function markGoogleRedirectPending({ inviteCode, serverTicket, pow } = {}) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      GOOGLE_REDIRECT_PENDING_KEY,
      JSON.stringify({
        inviteCode: inviteCode || undefined,
        // Google 新注册时序票据：须在跳转前签发，回跳后原样回传
        serverTicket: serverTicket || undefined,
        pow: pow || undefined,
        at: Date.now(),
      })
    );
  } catch {
    /* ignore quota */
  }
}

function readGoogleRedirectPending() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(GOOGLE_REDIRECT_PENDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearGoogleRedirectPending() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(GOOGLE_REDIRECT_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function hasPendingGoogleFirebaseRedirect() {
  return Boolean(readGoogleRedirectPending());
}

/**
 * 不强制 Redirect：One Tap → popup；仅 popup 被拦时才整页跳转。
 */
export function shouldForceGoogleFirebaseRedirect() {
  return false;
}

function shouldFallbackToRedirect(error) {
  const code = String(error?.code ?? '').trim();
  return code === 'auth/internal-error' || code === 'auth/popup-blocked';
}

function clientIdFromAuthUri(authUri) {
  try {
    const url = new URL(String(authUri || ''));
    return String(url.searchParams.get('client_id') || '').trim();
  } catch {
    return '';
  }
}

/**
 * One Tap 需要 OAuth Web Client ID。
 * 优先 env → 缓存 → getProjectConfig → createAuthUri（从 authUri 解析 client_id）。
 */
async function resolveGoogleWebClientId() {
  const fromEnv = String(process.env.VUE_APP_GOOGLE_OAUTH_CLIENT_ID || '').trim();
  if (fromEnv) return fromEnv;
  if (cachedGoogleWebClientId) return cachedGoogleWebClientId;

  const apiKey = String(process.env.VUE_APP_FIREBASE_API_KEY || '').trim();
  if (!apiKey || typeof fetch === 'undefined') return '';

  try {
    const res = await fetch(
      `https://www.googleapis.com/identitytoolkit/v3/relyingparty/getProjectConfig?key=${encodeURIComponent(
        apiKey
      )}`
    );
    if (res.ok) {
      const data = await res.json();
      const fromConfig = String(data?.clientId || data?.oauthClientId || '').trim();
      if (fromConfig) {
        cachedGoogleWebClientId = fromConfig;
        return fromConfig;
      }
    }
  } catch {
    /* continue */
  }

  try {
    const continueUri =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://deepsage.app';
    const res = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:createAuthUri?key=${encodeURIComponent(
        apiKey
      )}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ providerId: 'google.com', continueUri }),
      }
    );
    if (!res.ok) return '';
    const data = await res.json();
    const fromUri = clientIdFromAuthUri(data?.authUri);
    if (fromUri) {
      cachedGoogleWebClientId = fromUri;
    }
    return fromUri;
  } catch {
    return '';
  }
}

function loadGisScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('no window'));
  }
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (gisScriptPromise) return gisScriptPromise;

  gisScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('GIS script failed')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gisScriptPromise = null;
      reject(new Error('GIS script failed'));
    };
    document.head.appendChild(script);
  });
  return gisScriptPromise;
}

/**
 * GIS initialize 整页一次；credential 经模块 handler 转发，避免多次 initialize 警告与状态错乱。
 * @param {string} clientId
 */
async function ensureGisInitialized(clientId, loginHint) {
  await loadGisScript();
  if (!window.google?.accounts?.id) {
    throw Object.assign(new Error('ONE_TAP_UNAVAILABLE'), { code: 'one-tap/unavailable' });
  }
  const hint = String(loginHint || '').trim();
  if (gisInitializedClientId) {
    return gisInitializedClientId === clientId && gisInitializedLoginHint === hint;
  }

  window.google.accounts.id.initialize({
    client_id: clientId,
    auto_select: true,
    cancel_on_tap_outside: true,
    use_fedcm_for_prompt: true,
    ...(hint ? { login_hint: hint } : {}),
    callback: (response) => {
      const handler = oneTapCredentialHandler;
      if (typeof handler === 'function') handler(response);
    },
  });
  gisInitializedClientId = clientId;
  gisInitializedLoginHint = hint;
  return true;
}

/**
 * Google One Tap / FedCM：无 OAuth 大弹窗；失败/超时由上层 fallback popup。
 * @returns {Promise<{ googleToken: string, email?: string }>}
 */
async function signInWithGoogleOneTap({ syncFirebase = true, loginHint } = {}) {
  const clientId = await resolveGoogleWebClientId();
  if (!clientId) {
    throw Object.assign(new Error('ONE_TAP_NO_CLIENT_ID'), { code: 'one-tap/no-client-id' });
  }
  const hint = String(loginHint || '').trim();
  const configMatches = await ensureGisInitialized(clientId, hint);
  if (!configMatches) {
    throw Object.assign(new Error('ONE_TAP_CONFIG_MISMATCH'), {
      code: 'one-tap/config-mismatch',
      reason: 'login_hint_changed',
    });
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let softTimeoutId = 0;
    let hardTimeoutId = 0;

    const clearTimers = () => {
      window.clearTimeout(softTimeoutId);
      window.clearTimeout(hardTimeoutId);
    };

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimers();
      if (inFlightGoogleCancel) inFlightGoogleCancel = null;
      if (oneTapCredentialHandler) oneTapCredentialHandler = null;
      try {
        window.google?.accounts?.id?.cancel?.();
      } catch {
        /* ignore */
      }
      fn(value);
    };

    inFlightGoogleCancel = () => {
      finish(
        reject,
        Object.assign(new Error('GOOGLE_SIGNIN_CANCELLED'), {
          code: 'auth/popup-closed-by-user',
        })
      );
    };

    oneTapCredentialHandler = async (response) => {
      try {
        const googleToken = String(response?.credential ?? '').trim();
        if (!googleToken) {
          finish(reject, Object.assign(new Error('ONE_TAP_EMPTY'), { code: 'one-tap/empty' }));
          return;
        }
        if (syncFirebase) {
          try {
            const auth = getFirebaseAuth();
            const cred = GoogleAuthProvider.credential(googleToken);
            await signInWithCredential(auth, cred);
          } catch (err) {
            console.warn('[firebase] One Tap → Firebase credential sync failed', err);
          }
        }
        const email = emailFromJwt(googleToken) || undefined;
        if (email) rememberLastGoogleEmail(email);
        finish(resolve, { googleToken, email });
      } catch (err) {
        finish(reject, err);
      }
    };

    // 未展示：尽快 fallback。已展示则交给用户选账号 / 新增，不再用超时打断。
    softTimeoutId = window.setTimeout(() => {
      finish(reject, Object.assign(new Error('ONE_TAP_TIMEOUT'), { code: 'one-tap/timeout' }));
    }, ONE_TAP_PRE_DISPLAY_MS);

    hardTimeoutId = window.setTimeout(() => {
      finish(
        reject,
        Object.assign(new Error('ONE_TAP_HARD_TIMEOUT'), {
          code: 'one-tap/timeout',
          reason: 'hard_timeout',
        })
      );
    }, ONE_TAP_HARD_TIMEOUT_MS);

    window.google.accounts.id.prompt((notification) => {
      if (settled) return;
      if (notification?.isDisplayMoment?.()) {
        clearTimers();
        return;
      }
      if (notification?.isDismissedMoment?.()) {
        const dismissedReason = String(notification?.getDismissedReason?.() || 'dismissed').trim();
        // 用户明确关掉 / 点外侧：视为取消，不自动再开 popup
        if (
          dismissedReason === 'cancel' ||
          dismissedReason === 'cancel_called' ||
          dismissedReason === 'tap_outside'
        ) {
          finish(
            reject,
            Object.assign(new Error('ONE_TAP_DISMISSED'), {
              code: 'auth/popup-closed-by-user',
              reason: dismissedReason,
            })
          );
          return;
        }
        // 冷却、credential_returned 未进 callback 等：交给上层 popup
        finish(
          reject,
          Object.assign(new Error(`ONE_TAP_DISMISSED:${dismissedReason}`), {
            code: 'one-tap/skipped',
            reason: dismissedReason,
          })
        );
        return;
      }
      const skipped = notification?.isNotDisplayed?.() || notification?.isSkippedMoment?.();
      if (skipped) {
        const reason =
          notification?.getNotDisplayedReason?.() ||
          notification?.getSkippedReason?.() ||
          'skipped';
        if (reason === 'unregistered_origin') {
          finish(
            reject,
            Object.assign(new Error('ORIGIN_MISMATCH'), {
              code: 'auth/unauthorized-domain',
              reason,
            })
          );
          return;
        }
        finish(
          reject,
          Object.assign(new Error(`ONE_TAP_SKIPPED:${reason}`), {
            code: 'one-tap/skipped',
            reason,
          })
        );
      }
    });
  });
}

function isOneTapMiss(error) {
  const code = String(error?.code ?? '');
  return code.startsWith('one-tap/');
}

/**
 * Firebase Google 登录，返回 Google OAuth ID Token 供后端 /liyuan-auth/login/google。
 * remembered：One Tap → popup → redirect；chooser：popup → redirect。
 * @returns {Promise<{ googleToken: string, email?: string } | { redirected: true }>}
 */
export async function signInWithGoogleViaFirebase({
  inviteCode,
  forceRedirect = false,
  preferOneTap = true,
  allowPopup = true,
  flow = 'chooser',
  loginHint,
  /** @type {Promise<{serverTicket?: string, pow?: object}|null>|null} */
  timingPrep = null,
} = {}) {
  const auth = getFirebaseAuth();
  const requestedFlow = normalizeGoogleFlow(flow);
  const rememberedEmail = requestedFlow === 'remembered' ? String(loginHint || '').trim() : '';
  const mode = rememberedEmail ? 'remembered' : 'chooser';

  if (!forceRedirect && preferOneTap && mode === 'remembered') {
    if (shouldSkipGoogleOneTap()) {
      console.info('[firebase] skip Google One Tap on Edge, use popup');
    } else {
      try {
        return await signInWithGoogleOneTap({ loginHint: rememberedEmail });
      } catch (err) {
        if (isFirebaseAuthCancelled(err)) {
          throw err;
        }
        // origin 未登记时 popup 也会 400，不要再开弹窗
        if (isFirebaseUnauthorizedDomain(err)) {
          throw err;
        }
        if (!isOneTapMiss(err)) {
          console.warn('[firebase] Google One Tap failed, falling back to popup', err?.code || err);
        } else {
          console.info('[firebase] Google One Tap skipped', err?.reason || err?.code || err);
        }
        try {
          window.google?.accounts?.id?.cancel?.();
        } catch {
          /* ignore */
        }
      }
    }
  }

  if (!forceRedirect && allowPopup) {
    const provider = createGoogleProvider({ flow: mode, loginHint: rememberedEmail });
    try {
      const result = await signInWithPopup(auth, provider);
      const googleToken = extractGoogleIdToken(result);
      if (!googleToken) {
        throw new Error('Failed to obtain Google ID token');
      }
      const email = String(result.user?.email ?? '').trim() || undefined;
      if (email) rememberLastGoogleEmail(email);
      return { googleToken, email };
    } catch (err) {
      if (isFirebaseAuthCancelled(err)) {
        throw err;
      }
      if (!shouldFallbackToRedirect(err)) {
        throw err;
      }
      console.warn('[firebase] Google popup failed, falling back to redirect', err?.code || err);
    }
  }

  const provider = createGoogleProvider({ flow: mode, loginHint: rememberedEmail });
  let serverTicket;
  let pow;
  if (timingPrep) {
    try {
      const prep = await timingPrep;
      serverTicket = prep?.serverTicket || undefined;
      pow = prep?.pow || undefined;
    } catch {
      /* soft-fail：存量 Google 登录不依赖票据 */
    }
  }
  markGoogleRedirectPending({ inviteCode, serverTicket, pow });
  try {
    await signInWithRedirect(auth, provider);
  } catch (err) {
    clearGoogleRedirectPending();
    throw err;
  }
  return { redirected: true };
}

/**
 * 启动时幂等探测 Redirect 回跳；不依赖自有 pending 标记，兼容 sessionStorage 丢失。
 * @returns {Promise<null | { googleToken: string, email?: string, inviteCode?: string, serverTicket?: string, pow?: object }>}
 */
export function probeGoogleFirebaseRedirectResult() {
  if (!isFirebaseConfigured()) return Promise.resolve(null);
  if (googleRedirectResultPromise) return googleRedirectResultPromise;

  const pending = readGoogleRedirectPending();
  googleRedirectResultPromise = (async () => {
    const auth = getFirebaseAuth();
    let result = null;
    try {
      result = await getRedirectResult(auth);
    } catch (err) {
      clearGoogleRedirectPending();
      // 普通冷启动不应被一次 Firebase 探测错误污染；明确发起过 Redirect 才交给 UI。
      if (pending) throw err;
      console.warn('[firebase] Google redirect probe failed without pending state', err);
      return null;
    }
    clearGoogleRedirectPending();
    if (!result) return null;

    const googleToken = extractGoogleIdToken(result);
    if (!googleToken) {
      throw new Error('Failed to obtain Google ID token');
    }
    const email = String(result.user?.email ?? '').trim() || undefined;
    if (email) rememberLastGoogleEmail(email);
    return {
      googleToken,
      email,
      inviteCode: pending?.inviteCode || undefined,
      serverTicket: pending?.serverTicket || undefined,
      pow: pending?.pow || undefined,
    };
  })();
  return googleRedirectResultPromise;
}

/** App 启动层单次消费；AuthDialog 不再续接 Redirect。 */
export async function consumeGoogleFirebaseRedirectResult() {
  let result = null;
  try {
    result = await probeGoogleFirebaseRedirectResult();
  } catch (err) {
    if (googleRedirectResultConsumed) return null;
    googleRedirectResultConsumed = true;
    throw err;
  }
  if (googleRedirectResultConsumed) return null;
  googleRedirectResultConsumed = true;
  return result;
}

/** 供 UI 映射 Firebase 错误 → authDialog i18n key（不含 authDialog. 前缀） */
export function resolveFirebaseAuthErrorKey(err) {
  const code = String(err?.code ?? '').trim();
  if (code === 'auth/unauthorized-domain') return 'googleUnauthorizedDomain';
  if (code === 'auth/popup-blocked') return 'googlePopupBlocked';
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return 'googleFailed';
  }
  if (code === 'auth/internal-error') return 'googleInternalError';
  if (code === 'auth/network-request-failed') return 'googleNetworkError';
  if (code === 'auth/operation-not-allowed') return 'googleNotEnabled';
  return null;
}

export function isFirebaseAuthCancelled(error) {
  const code = error?.code || '';
  return code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request';
}

export function isFirebaseUnauthorizedDomain(error) {
  return error?.code === 'auth/unauthorized-domain';
}

export function isFirebasePopupBlocked(error) {
  return error?.code === 'auth/popup-blocked';
}

registerAuthTeardown(() => {
  disableGoogleOneTapAutoSelect();
});
